import { useMemo } from 'react';
import { InputSize, VisualSlotType } from '@/shared/types/types';

interface UseInputSlotsProps {
  size?: InputSize;
  leftSlot?: VisualSlotType | null;
  rightSlot?: VisualSlotType | null;
  extraSlot?: VisualSlotType | null;
  /** Slot de limpiar (X) entre `rightSlot` y `extraSlot`. */
  hasClearSlot?: boolean;
}

export const useInputSlots = ({
  size = 'md',
  leftSlot,
  rightSlot,
  extraSlot,
  hasClearSlot = false,
}: UseInputSlotsProps) => {
  const hasLeftSlot = !!leftSlot;
  const hasRightSlot = !!rightSlot;
  const hasExtraSlot = !!extraSlot;

  /** X + chevron sin `rightSlot` (p. ej. Dropdown solo con icono izquierdo): hace falta más `pr` y `right` en la X. */
  const dualClearChevronNoRight = hasClearSlot && hasExtraSlot && !hasRightSlot;

  const trailingCount = (hasRightSlot ? 1 : 0) + (hasClearSlot ? 1 : 0) + (hasExtraSlot ? 1 : 0);

  /**
   * Derecha: carril con `ui-gap-x-8` (`space_8`) + inset; `lg` usa más `pl`/`pr` (control alto, mismo ritmo 8px).
   */
  const paddingClasses = useMemo(() => {
    const isLg = size === 'lg';

    const leftBySize: Record<InputSize, string> = {
      xs: hasLeftSlot ? 'ui-pl-7.5' : 'ui-p-8',
      sm: hasLeftSlot ? 'ui-pl-7.5' : 'ui-pl-8',
      md: hasLeftSlot ? 'ui-pl-7.5' : 'ui-pl-8',
      lg: hasLeftSlot ? 'ui-pl-32' : 'ui-pl-12',
    };

    const prDefault = isLg ? 'ui-pr-12' : 'ui-pr-8';

    const rightForTrail = (n: number): string => {
      if (n <= 0) return prDefault;
      if (isLg) {
        if (n === 1) return 'ui-pr-48';
        if (n === 2) return 'ui-pr-64';
        return 'ui-pr-[100px]';
      }
      if (n === 1) return 'ui-pr-32';
      if (n === 2) return 'ui-pr-64';
      return 'ui-pr-[100px]';
    };

    const right = dualClearChevronNoRight
      ? isLg
        ? 'ui-pr-78'
        : 'ui-pr-64'
      : rightForTrail(trailingCount);

    const left = leftBySize[size] ?? leftBySize.md;

    return `${left} ${right}`.trim();
  }, [size, hasLeftSlot, trailingCount, dualClearChevronNoRight]);

  /**
   * Posición del icono izquierdo: usar claves del `figmaSpacingScale` (`space_8` = 8px, `space_10` = 10px).
   * `ui-left-2` sería `space_2` (~2px), no 8px.
   */
  const getLeftSlotPosition = useMemo(() => {
    const positions: Record<InputSize, string> = {
      xs: 'ui-left-8',
      sm: 'ui-left-10',
      md: 'ui-left-10',
      lg: 'ui-left-10',
    };
    return positions[size] ?? positions.sm;
  }, [size]);

  return {
    hasLeftSlot,
    hasRightSlot,
    hasExtraSlot,
    hasClearSlot,
    paddingClasses,
    getLeftSlotPosition,
  };
};
